import React, { useState } from 'react';
import { TextInput } from '../../Shared/Component/forms/TextInput';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { PasswordInput } from '../../Shared/Component/forms/PasswordInput';
import { getPasswordValidationError } from '../../Shared/Utils/validationPolicy';
import { useValidationPolicy } from '../../Shared/Hooks/useValidationPolicy';
import { PasswordPolicyHints } from '../../Shared/Component/forms/PasswordPolicyHints';
import { CreateUserRequestForRecruiterRegistration } from '../../App/Types/UsersType';
import { createRecruiterUserFromInvite, createUser } from '../../App/Services/UserService';
import { useSearchParams } from 'react-router-dom';

function RecruiterRegistraionComponent() {

  const [searchParams] = useSearchParams();

  // ✅🔥 FREEZE values (runs ONLY once)
  const [initialEmail] = useState(() => searchParams.get("email") || "");
  const [initialUserId] = useState(() => searchParams.get("userId") || "");

  const {
    values,
    handleInputChange,
  } = useFormController<CreateUserRequestForRecruiterRegistration>({
    recruiterEmail: initialEmail,
    password: "",
    role: "RECRUITER",
    recruiterFirstName: "",
    recruiterLastName: "",
  });

  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [loading, setLoading] = useState(false);
  const { policy } = useValidationPolicy();

  const handleSubmit = async () => {
    const passwordError = getPasswordValidationError(values.password, policy);
    if (passwordError) {
      setAlert({
        variant: "error",
        title: "Weak Password",
        message: passwordError,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      return;
    }

    setLoading(true);
    setAlert(null);

    try {
      const response = await createRecruiterUserFromInvite({
        ...values,
        userId: initialUserId, // ✅ always original
      });

      setAlert({
        variant: response.success ? "success" : "error",
        title: response.success ? "Success...!" : "Registration Error",
        message: response.message,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });

    } catch (err) {
      setAlert({
        variant: "error",
        title: "System Error",
        message: "Something went wrong.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        
        <div className="bg-indigo-600 p-6 text-center">
          <h2 className="text-2xl font-bold text-white">Join the Team</h2>
          <p className="text-indigo-100 text-sm mt-1">
            Complete your recruiter profile
          </p>
        </div>

        <div className="p-8 space-y-5">

          {alert && (
            <Alert 
              variant={alert.variant} 
              title={alert.title} 
              message={alert.message} 
              onClose={() => setAlert(null)} 
            />
          )}

          <div className="space-y-4">

            {/* 🔒 Email LOCKED permanently */}
            <TextInput
              label="Email Address"
              name="recruiterEmail"
              value={values.recruiterEmail}
              onChange={() => {}} // disabled
              readOnly={true}
              className="bg-slate-100 cursor-not-allowed"
            />

            <div className="grid grid-cols-2 gap-4">
              <TextInput
                label="First Name"
                name="recruiterFirstName"
                placeholder="Jane"
                value={values.recruiterFirstName}
                onChange={handleInputChange("recruiterFirstName")}
              />
              <TextInput
                label="Last Name"
                name="recruiterLastName"
                placeholder="Doe"
                value={values.recruiterLastName}
                onChange={handleInputChange("recruiterLastName")}
              />
            </div>

            <PasswordInput
              label="Create Password"
              name="password"
              placeholder="••••••••"
              value={values.password}
              onChange={handleInputChange("password")}
            />
            <PasswordPolicyHints password={values.password} policy={policy} />

          </div>

          <button 
            onClick={handleSubmit}
            disabled={loading}
            className={`w-full mt-6 px-6 py-3 text-white font-semibold rounded-lg shadow-md 
              ${loading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} 
              transition-all duration-200`}
          >
            {loading ? "Processing..." : "Activate Your Account"}
          </button>

          <p className="text-center text-xs text-slate-400 mt-4">
            By activating, you agree to our Terms of Service.
          </p>

        </div>
      </div>
    </div>
  );
}

export default RecruiterRegistraionComponent;